/**
 * Local-storage backed preference bag.
 *
 * Every user action the site remembers goes through here. It is deliberately tolerant:
 * private-mode browsers and disabled storage fall back to an in-memory map rather than
 * throwing, so the app never breaks over a preference.
 */

export const STORAGE_KEY = "upsorok:prefs";

export const DEFAULT_PREFS = {
  theme: "system",
  view: "grid",
  sort: "rating",
  pageSize: 24,
  recentSearches: [],
  recentlyViewed: [],
  favorites: [],
  dismissedTips: [],
  lastCuisine: null,
  lastCity: null,
};

function memoryFallback() {
  const map = new Map();
  return {
    getItem: (key) => (map.has(key) ? map.get(key) : null),
    setItem: (key, value) => void map.set(key, value),
    removeItem: (key) => void map.delete(key),
  };
}

export function safeStorage(candidate) {
  const store = candidate ?? (typeof localStorage !== "undefined" ? localStorage : null);
  if (!store) return memoryFallback();
  try {
    const probe = "__upsorok_probe__";
    store.setItem(probe, "1");
    store.removeItem(probe);
    return store;
  } catch {
    return memoryFallback();
  }
}

export function readPrefs(store) {
  const storage = safeStorage(store);
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    return mergePrefs(DEFAULT_PREFS, JSON.parse(raw));
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

export function writePrefs(prefs, store) {
  const storage = safeStorage(store);
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    /* storage full or unavailable — preferences simply don't persist */
  }
  return prefs;
}

/** Shallow merge that keeps unknown keys and never lets a bad type through. */
export function mergePrefs(base, patch) {
  const result = { ...base };
  for (const [key, value] of Object.entries(patch ?? {})) {
    if (value === undefined || value === null) continue;
    const expected = DEFAULT_PREFS[key];
    if (Array.isArray(expected) && !Array.isArray(value)) continue;
    if (typeof expected === "number" && typeof value !== "number") continue;
    result[key] = value;
  }
  return result;
}

/** Most-recent-first, de-duplicated, bounded list helper used for searches and views. */
export function pushRecent(list, value, limit = 12) {
  if (value === undefined || value === null || value === "") return list ?? [];
  const key = typeof value === "object" ? value.id : value;
  const rest = (list ?? []).filter((item) => (typeof item === "object" ? item.id : item) !== key);
  return [value, ...rest].slice(0, limit);
}

export function toggleInList(list, value) {
  const current = list ?? [];
  return current.includes(value) ? current.filter((item) => item !== value) : [...current, value];
}

/** Resolves "system" against the OS preference so the DOM always gets a concrete theme. */
export function resolveTheme(theme, prefersDark = false) {
  if (theme === "dark" || theme === "light") return theme;
  return prefersDark ? "dark" : "light";
}
